// ============================================================
// Moteur PUR des contrôles de préparation de paie — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-payroll-checks.spec.ts.
// ============================================================
//
// L'équivalent du bandeau « J'ai identifié des éléments à vérifier pour préparer la paie »
// observé en démo — mais SANS IA : ce sont des règles déterministes, explicables et testables.
// Un gérant doit pouvoir comprendre POURQUOI une ligne remonte, et le résultat doit être le
// même à chaque exécution. Une boîte noire probabiliste n'a pas sa place dans un calcul de paie.
//
// Chaque anomalie porte de quoi NAVIGUER vers l'élément fautif (employé, date), pour que le
// bandeau soit actionnable et pas seulement informatif.

import { HrAbsence, HrEmployee } from '@app/shared/_models/hr';
import { HR_ABSENCE_STATE_REQUESTED } from '@app/shared/constants';
import { ClockAnomaly, UnclosedDay } from './hr-timesheet';
import { HrTimeSource, PayrollRow } from './hr-payroll';

export type PayrollIssueCode =
	| 'contract-missing'					// profil incomplet : pas de paie fiable possible
	| 'absence-missing-proof'			// arrêt maladie sans justificatif déposé
	| 'clock-anomaly'							// entrée sans sortie, sortie sans entrée
	| 'day-unclosed'							// journée passée jamais close (mode planning)
	| 'pending-leave-in-period'		// demande d'absence non décidée sur la période
	| 'overtime-unusual'					// heures sup au-delà d'un seuil de vigilance
	| 'no-hours'									// salarié actif sans aucune heure sur la période
	| 'period-unlocked';					// période non verrouillée à l'export

export type PayrollIssueLevel = 'blocking' | 'warning' | 'info';

export interface PayrollIssue {
	code: PayrollIssueCode;
	level: PayrollIssueLevel;
	id_employee: number | null;		// null = anomalie de périmètre (période non verrouillée)
	name: string;
	detail: string;
	dat: string | null;						// date à ouvrir dans le planning, si applicable
}

export interface PayrollCheckContext {
	rows: PayrollRow[];
	employees: HrEmployee[];
	absences: HrAbsence[];					// absences chevauchant la période, TOUS états
	absenceTypes: { id?: number; name?: string; data?: any }[];
	anomalies: ClockAnomaly[];			// sortie de pairClocks
	// Journées passées jamais closes (sortie de unclosedDays) — vide en mode pointeuse.
	unclosed?: UnclosedDay[];
	documentsByAbsence: Set<number>;	// ids d'absences ayant un justificatif (L5) — vide sans GED
	from: string;
	to: string;
	fullyLocked: boolean;
	overtimeAlertMin: number;				// seuil de vigilance des heures sup (minutes / période)
	source?: HrTimeSource;					// mode de décompte de l'établissement — défaut : le planning
}

/// Passe en revue la période et rend les points à vérifier, les plus graves d'abord.
export function payrollIssues(ctx: PayrollCheckContext): PayrollIssue[] {
	const out: PayrollIssue[] = [];
	const nameOf = (id: number) => ctx.rows.find(r => r.id_employee === id)?.name ?? ('Employé #' + id);

	// --- Profils incomplets : sans contrat, aucun écart ni heure sup n'a de sens ---
	for (const row of ctx.rows) {
		if (row.incomplete.length) {
			out.push({
				code: 'contract-missing', level: 'blocking',
				id_employee: row.id_employee, name: row.name,
				detail: 'Profil incomplet pour la paie : ' + row.incomplete.join(', '),
				dat: null,
			});
		}
	}

	// --- Justificatifs manquants : le cas montré en démo (« Perrine a un arrêt maladie ») ---
	// Un type d'absence porte `requires_proof: true` quand il exige une pièce.
	const proofTypes = new Set(
		ctx.absenceTypes.filter(t => t.data?.requires_proof === true).map(t => t.id));
	for (const a of ctx.absences) {
		if (!proofTypes.has(a.typ ?? -1)) continue;
		if (a.dateend < ctx.from || a.datestart > ctx.to) continue;
		if (ctx.documentsByAbsence.has(a.id)) continue;
		const typName = ctx.absenceTypes.find(t => t.id === a.typ)?.name ?? 'Absence';
		out.push({
			code: 'absence-missing-proof', level: 'warning',
			id_employee: a.id_employee, name: nameOf(a.id_employee),
			detail: typName + ' du ' + fr(a.datestart) + ' au ' + fr(a.dateend) + ' — aucun justificatif déposé',
			dat: a.datestart,
		});
	}

	// --- Anomalies de pointage : un temps de travail non fiable fausse toute la paie ---
	// Sans badgeuse (mode planning), ces anomalies ne pilotent aucune heure payée : les signaler
	// reviendrait à faire crier le rapport pour une pointeuse que personne n'utilise.
	for (const an of (ctx.source === 'clock' ? ctx.anomalies : [])) {
		if (an.dat < ctx.from || an.dat > ctx.to) continue;
		out.push({
			code: 'clock-anomaly', level: 'warning',
			id_employee: an.id_employee, name: nameOf(an.id_employee),
			detail: an.detail + ' le ' + fr(an.dat),
			dat: an.dat,
		});
	}

	// --- Journées jamais closes : le pendant du contrôle précédent, en mode PLANNING ---
	// Une journée passée sans clôture est payée sur l'horaire prévu, sans que personne n'ait dit
	// qu'elle s'était passée ainsi. Un planning n'est pas un décompte du temps travaillé
	// (L3171-2) : chaque journée non arbitrée doit se voir AVANT que la paie parte. En mode
	// pointeuse la liste arrive vide (unclosedDays) — le badge est l'enregistrement.
	const unclosedByEmp = new Map<number, UnclosedDay[]>();
	for (const u of (ctx.unclosed ?? [])) {
		if (u.dat < ctx.from || u.dat > ctx.to) continue;
		const list = unclosedByEmp.get(u.id_employee);
		if (list) list.push(u); else unclosedByEmp.set(u.id_employee, [u]);
	}
	for (const [id, days] of unclosedByEmp) {
		const declared = days.filter(d => d.declared).length;
		out.push({
			code: 'day-unclosed', level: 'warning',
			id_employee: id, name: nameOf(id),
			// Précision qui compte : une journée DÉCLARÉE est retenue à la déclaration du salarié
			// (retainedDayMinutes fait foi de toute validation, validée ou non), pas à l'horaire
			// planifié — dire le contraire ferait chercher le mauvais chiffre au comptable.
			detail: days.length + ' journée' + (days.length > 1 ? 's' : '') + ' sans clôture — l\'horaire planifié fait foi'
				+ (declared ? ' (dont ' + declared + ' déclarée' + (declared > 1 ? 's' : '')
					+ ' : la déclaration fait foi en attendant la validation)' : ''),
			dat: days[0].dat,
		});
	}

	// --- Demandes d'absence encore en attente : décider AVANT de figer la paie ---
	for (const a of ctx.absences) {
		if (a.state !== HR_ABSENCE_STATE_REQUESTED) continue;
		if (a.dateend < ctx.from || a.datestart > ctx.to) continue;
		const typName = ctx.absenceTypes.find(t => t.id === a.typ)?.name ?? 'Absence';
		out.push({
			code: 'pending-leave-in-period', level: 'warning',
			id_employee: a.id_employee, name: nameOf(a.id_employee),
			detail: typName + ' du ' + fr(a.datestart) + ' au ' + fr(a.dateend) + ' — demande non décidée',
			dat: a.datestart,
		});
	}

	// --- Heures sup inhabituelles : signaler, pas juger ---
	for (const row of ctx.rows) {
		if (ctx.overtimeAlertMin > 0 && row.overtime_min >= ctx.overtimeAlertMin) {
			out.push({
				code: 'overtime-unusual', level: 'info',
				id_employee: row.id_employee, name: row.name,
				detail: hours(row.overtime_min) + ' d\'heures supplémentaires sur la période',
				dat: null,
			});
		}
	}

	// --- Salarié actif sans aucune heure : oubli de planning ou départ non saisi ---
	for (const row of ctx.rows) {
		const emp = ctx.employees.find(e => e.id === row.id_employee);
		if (!emp || emp.is_active === false) continue;
		if (row.paid_min === 0 && row.absence_days === 0) {
			out.push({
				code: 'no-hours', level: 'info',
				id_employee: row.id_employee, name: row.name,
				detail: 'Aucune heure ni absence sur la période — planning oublié, ou sortie non saisie ?',
				dat: null,
			});
		}
	}

	// --- Période non verrouillée : l'export peut encore bouger sous les pieds du comptable ---
	if (!ctx.fullyLocked) {
		out.push({
			code: 'period-unlocked', level: 'info',
			id_employee: null, name: '',
			detail: 'La période n\'est pas verrouillée : les plannings'
				+ (ctx.source === 'clock' ? ' et pointages restent modifiables' : ' restent modifiables'),
			dat: null,
		});
	}

	const rank: Record<PayrollIssueLevel, number> = { blocking: 0, warning: 1, info: 2 };
	return out.sort((a, b) => rank[a.level] - rank[b.level]);
}

/// Compte les anomalies par niveau — alimente les pastilles du bandeau
/// (« 10 absences à vérifier », « 1 anomalie de planning »).
export function payrollIssueCounts(issues: PayrollIssue[]): Record<PayrollIssueLevel, number> {
	return {
		blocking: issues.filter(i => i.level === 'blocking').length,
		warning: issues.filter(i => i.level === 'warning').length,
		info: issues.filter(i => i.level === 'info').length,
	};
}

/// Résumé en une phrase, pour le bandeau du rapport.
export function payrollIssueSummary(issues: PayrollIssue[]): string {
	const c = payrollIssueCounts(issues);
	if (!issues.length) return 'Rien à signaler : la période est prête pour la paie.';

	const parts: string[] = [];
	if (c.blocking) parts.push(c.blocking + ' profil' + (c.blocking > 1 ? 's' : '') + ' à compléter');
	if (c.warning) parts.push(c.warning + ' point' + (c.warning > 1 ? 's' : '') + ' à vérifier');
	if (c.info) parts.push(c.info + ' information' + (c.info > 1 ? 's' : ''));
	return parts.join(' · ');
}

function fr(dat: string): string {
	return dat ? dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4) : '';
}

function hours(min: number): string {
	const h = Math.floor(min / 60);
	const m = min % 60;
	return h + 'h' + (m > 0 ? (m < 10 ? '0' + m : '' + m) : '');
}
